import Table, { type TableOptions } from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import { type Node as PMNode } from '@tiptap/pm/model';
import { Plugin, type Transaction } from '@tiptap/pm/state';
import {
  type Dispatch,
  type NodeViewRendererProps,
  type RawCommands,
  ReactNodeViewRenderer,
} from '@tiptap/react';
import { CellSelection } from 'prosemirror-tables';

import { ContentTableView } from './content-table-view';

export interface ContentTableCellHoverEvent extends CustomEvent {
  detail: {
    width: number;
    height: number;
    top: number;
    left: number;
    node: PMNode;
  };
}

export enum ContentTableEvent {
  CellHover = 'content-table-cell-hover',
}

function tableCellNodeView(tag: 'td' | 'th', args: NodeViewRendererProps) {
  const { node } = args;
  const cell = document.createElement(tag);
  const bgColor = tag === 'th' ? 'bg-branding-gray' : 'bg-white';
  cell.className = `min-h-[80px] min-w-[100px] border p-1 ${bgColor} border-branding-gray`;
  const contentDOM = document.createElement('div');
  contentDOM.className = 'p-1';
  contentDOM.dataset.insideTable = 'true';
  contentDOM.addEventListener('dragstart', function (event) {
    event?.preventDefault();
  });
  cell.appendChild(contentDOM);
  cell.addEventListener('mouseenter', function (ev) {
    let table: HTMLElement | null = cell;
    while (table != null && table.tagName.toLowerCase() !== 'table') {
      table = table.parentElement;
    }
    if (table != null && ev.target != null) {
      const target = ev.target as HTMLElement;
      const customEvent = new CustomEvent(ContentTableEvent.CellHover, {
        bubbles: true,
        detail: {
          width: target.offsetWidth,
          height: target.offsetHeight,
          left: target.offsetLeft,
          top: target.offsetTop,
          node,
        },
      });
      table.dispatchEvent(customEvent);
    }
  });
  return {
    dom: cell,
    contentDOM,
  };
}

export type ContentTableOptions = TableOptions & {
  draggable: boolean;
};

export const ContentTable = Table.extend({
  addNodeView() {
    return ReactNodeViewRenderer(ContentTableView);
  },
  draggable: true,
  addOptions(): ContentTableOptions {
    return {
      ...(this.parent == null ? {} : this.parent()),
      draggable: true,
    } as ContentTableOptions;
  },
});

export const ContentTableHeader = TableHeader.extend({
  addOptions() {
    return {
      ...this.parent?.(),
      HTMLAttributes: {
        class: '',
      },
    };
  },
  addNodeView() {
    return function (args) {
      return tableCellNodeView('th', args);
    };
  },
});

export const ContentTableCell = TableCell.extend({
  addOptions() {
    return {
      ...this.parent?.(),
      HTMLAttributes: {
        class: '',
      },
    };
  },
  addNodeView() {
    return function (args) {
      return tableCellNodeView('td', args);
    };
  },
  addCommands() {
    return {
      selectRow: function (cell: number) {
        return function (args: { tr: Transaction; dispatch: Dispatch }) {
          const { tr, dispatch } = args;
          if (dispatch == null) {
            return false;
          }
          const position = tr.doc.resolve(cell);
          const rowSelection = CellSelection.rowSelection(position);
          tr.setSelection(rowSelection);
          return true;
        };
      },
      selectColumn: function (cell: number) {
        return function (args: { tr: Transaction; dispatch: Dispatch }) {
          const { tr, dispatch } = args;
          if (dispatch == null) {
            return false;
          }
          const position = tr.doc.resolve(cell);
          const columnSelection = CellSelection.colSelection(position);
          tr.setSelection(columnSelection);
          return true;
        };
      },
    } as Partial<RawCommands>;
  },
  addProseMirrorPlugins() {
    let plugins: Plugin[] = [];
    if (typeof TableCell.config?.addProseMirrorPlugins === 'function') {
      plugins = TableCell.config.addProseMirrorPlugins.bind(this)();
    }
    const tableCellPlugin = new Plugin({
      appendTransaction: (transactions, _oldState, newState) => {
        if (!transactions.some((tr) => tr.docChanged)) {
          return null;
        }
        const { tr } = newState;
        let modified = false;
        newState.doc.descendants((node, pos) => {
          if (node.type.name === 'paragraph') {
            const $pos = newState.doc.resolve(pos);
            let insideTableCell = false;
            for (let i = $pos.depth; i > 0; i--) {
              const ancestor = $pos.node(i);
              if (ancestor.type.name === 'tableCell' || ancestor.type.name === 'tableHeader') {
                insideTableCell = true;
                break;
              }
            }
            if (insideTableCell && node.attrs.isDirectChildOfDoc !== false) {
              tr.setNodeMarkup(pos, undefined, {
                ...node.attrs,
                isDirectChildOfDoc: false,
              });
              modified = true;
            }
          }
          return true;
        });
        return modified ? tr : null;
      },
    });
    return [...plugins, tableCellPlugin];
  },
});
